import React, { useState } from 'react';
import { tickets as ticketsApi, jobs as jobsApi } from '../services/api';
import { AGENTS } from '../context/AgentContext';

const STATUS_BADGE = {
  completed: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', label: 'Completed' },
  running: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', label: 'Running' },
  failed: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: 'Failed' },
  pending: { bg: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', label: 'Pending' },
  stuck: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: 'Stuck' },
};

const STUCK_THRESHOLD_MINUTES = 20;

/**
 * Check if a job appears stuck (running too long without updates).
 */
function isJobStuck(job) {
  if (!job || job.status !== 'running') return false;
  const started = job.started_at ? new Date(job.started_at) : null;
  if (!started) return false;
  const elapsed = (Date.now() - started.getTime()) / 1000 / 60;
  return elapsed > STUCK_THRESHOLD_MINUTES;
}

/**
 * Get the relevant job for an agent.
 */
function getAgentJob(task, agentId) {
  if (agentId === 'plan') return task.plan_job;
  if (agentId === 'implement') return task.job;
  if (agentId === 'review') return task.review_job;
  return null;
}

const AgentDetailPanel = ({ task, selectedAgent, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualPrUrl, setManualPrUrl] = useState('');
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const agentDef = AGENTS.find(a => a.id === selectedAgent);
  const agentStatus = task.pipeline?.[selectedAgent]?.status || 'pending';
  const agentJob = getAgentJob(task, selectedAgent);
  const stuck = agentStatus === 'running' && isJobStuck(agentJob);
  const displayStatus = stuck ? 'stuck' : agentStatus;
  const badge = STATUS_BADGE[displayStatus] || STATUS_BADGE.pending;

  const handleEnhance = async () => {
    setLoading(true);
    setError('');
    try {
      await ticketsApi.enhance(task.id);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enhance description');
    } finally {
      setLoading(false);
    }
  };

  const handlePlan = async () => {
    setLoading(true);
    setError('');
    try {
      await ticketsApi.submitPlanJob(task.id);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit plan job');
    } finally {
      setLoading(false);
    }
  };

  const handleImplement = async () => {
    setLoading(true);
    setError('');
    try {
      await ticketsApi.submitJob(task.id);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit implementation job');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    setLoading(true);
    setError('');
    try {
      await ticketsApi.submitReviewJob(task.id);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit review job');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrUrl = async () => {
    if (!manualPrUrl.trim()) return;
    setLoading(true);
    setError('');
    try {
      await ticketsApi.setPrUrl(task.id, manualPrUrl.trim());
      setManualPrUrl('');
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set PR URL');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAgent = async () => {
    setLoading(true);
    setError('');
    try {
      await ticketsApi.resetAgent(task.id, selectedAgent);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset agent');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = async () => {
    if (!agentJob) return;
    setLoadingLogs(true);
    try {
      const response = await jobsApi.getLogs(agentJob.id);
      setLogs(response.data.logs);
      setShowLogs(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const renderLogs = () => {
    if (!showLogs || !logs) return null;
    return (
      <div style={{
        background: '#0d1117',
        border: '1px solid var(--border-color, #334155)',
        borderRadius: '8px',
        padding: '12px',
        marginTop: '12px',
        maxHeight: '400px',
        overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>Worker Logs</span>
          <button
            onClick={() => setShowLogs(false)}
            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '14px' }}
          >&times;</button>
        </div>
        <pre style={{
          fontSize: '11px',
          lineHeight: '1.5',
          color: '#e2e8f0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          margin: 0,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{logs}</pre>
      </div>
    );
  };

  const renderStuckMessage = () => {
    if (!stuck && agentStatus !== 'failed') return null;

    const elapsed = agentJob?.started_at
      ? Math.floor((Date.now() - new Date(agentJob.started_at).getTime()) / 1000 / 60)
      : null;

    return (
      <div style={{
        background: agentStatus === 'failed' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)',
        border: `1px solid ${agentStatus === 'failed' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`,
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
      }}>
        <div style={{
          fontSize: '13px',
          color: agentStatus === 'failed' ? '#ef4444' : '#f59e0b',
          marginBottom: '8px',
          fontWeight: 500,
        }}>
          {agentStatus === 'failed'
            ? `Failed: ${agentJob?.error_message || 'Unknown error'}`
            : `Job appears stuck — running for ${elapsed}m with no response`}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="agent-detail__action-btn"
            onClick={handleResetAgent}
            disabled={loading}
            style={{
              background: agentStatus === 'failed' ? '#ef4444' : '#f59e0b',
              flex: 1,
              fontSize: '13px',
              padding: '8px 12px',
            }}
          >
            {loading ? 'Resetting...' : `Reset ${agentDef?.label} Agent`}
          </button>
          {agentJob && (
            <button
              className="agent-detail__action-btn"
              onClick={handleViewLogs}
              disabled={loadingLogs}
              style={{
                background: 'var(--bg-tertiary, #334155)',
                flex: 1,
                fontSize: '13px',
                padding: '8px 12px',
              }}
            >
              {loadingLogs ? 'Loading...' : 'View Logs'}
            </button>
          )}
        </div>
        {renderLogs()}
      </div>
    );
  };

  const renderAgentContent = () => {
    switch (selectedAgent) {
      case 'enhance': {
        const planStarted = task.pipeline?.plan?.status && task.pipeline.plan.status !== 'pending';
        return (
          <div className="agent-detail__content">
            <div className="agent-detail__info">
              <span className="agent-detail__info-label">Description</span>
              <p className="agent-detail__info-value" style={{ fontSize: '13px', lineHeight: '1.6' }}>
                {task.description
                  ? (task.description.length > 200 ? task.description.substring(0, 200) + '...' : task.description)
                  : 'No description yet'}
              </p>
            </div>
            {agentStatus === 'pending' && !planStarted && (
              <button
                className="agent-detail__action-btn"
                onClick={handleEnhance}
                disabled={loading || !task.description}
                style={{ background: agentDef.color }}
              >
                {loading ? 'Enhancing...' : 'Run Enhance'}
              </button>
            )}
            {agentStatus === 'pending' && planStarted && (
              <div className="agent-detail__completed-msg">
                Skipped — plan already in progress
              </div>
            )}
            {agentStatus === 'completed' && (
              <div className="agent-detail__completed-msg">
                Description has been enhanced by AI
              </div>
            )}
          </div>
        );
      }

      case 'plan':
        return (
          <div className="agent-detail__content">
            {renderStuckMessage()}
            {task.plan_job && !stuck && agentStatus !== 'failed' && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Plan Job</span>
                <p className="agent-detail__info-value">
                  Status: {task.plan_job.status}
                  {task.plan_job.error_message && (
                    <span className="agent-detail__error-inline"> — {task.plan_job.error_message}</span>
                  )}
                </p>
              </div>
            )}
            {task.plan && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Plan Preview</span>
                <pre className="agent-detail__plan-preview">
                  {task.plan.substring(0, 500)}{task.plan.length > 500 ? '...' : ''}
                </pre>
              </div>
            )}
            {agentStatus === 'pending' && (
              <button
                className="agent-detail__action-btn"
                onClick={handlePlan}
                disabled={loading || !!task.plan_job}
                style={{ background: agentDef.color }}
              >
                {loading ? 'Submitting...' : 'Create Plan'}
              </button>
            )}
            {agentStatus === 'running' && !stuck && (
              <div className="agent-detail__running-msg">
                Plan generation in progress...
              </div>
            )}
          </div>
        );

      case 'implement': {
        const changesRequested = task.pipeline?.review?.changes_requested;
        return (
          <div className="agent-detail__content">
            {renderStuckMessage()}
            {task.job && !stuck && agentStatus !== 'failed' && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Implementation Job</span>
                <p className="agent-detail__info-value">
                  Status: {task.job.status}
                  {task.job.branch_name && (
                    <><br/>Branch: <code>{task.job.branch_name}</code></>
                  )}
                  {task.job.error_message && (
                    <span className="agent-detail__error-inline"> — {task.job.error_message}</span>
                  )}
                </p>
              </div>
            )}
            {task.job?.pr_url && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Pull Request</span>
                <a
                  href={task.job.pr_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="agent-detail__pr-link"
                >
                  View Pull Request
                </a>
              </div>
            )}
            {agentStatus === 'pending' && changesRequested && (
              <div className="agent-detail__info" style={{ color: '#f59e0b', marginBottom: '12px' }}>
                Changes requested — re-implementation needed
              </div>
            )}
            {agentStatus === 'pending' && (
              <button
                className="agent-detail__action-btn"
                onClick={handleImplement}
                disabled={loading || !!task.job || !task.plan}
                style={{ background: agentDef.color }}
              >
                {loading ? 'Submitting...' : changesRequested ? 'Re-implement' : 'Submit Job'}
              </button>
            )}
            {agentStatus === 'running' && !stuck && (
              <div className="agent-detail__running-msg">
                Implementation in progress...
              </div>
            )}
            {agentStatus === 'completed' && !task.job?.pr_url && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.08)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px',
              }}>
                <div style={{ fontSize: '13px', color: '#f59e0b', marginBottom: '8px', fontWeight: 500 }}>
                  Implementation completed but no changes were pushed
                </div>
                <button
                  className="agent-detail__action-btn"
                  onClick={handleResetAgent}
                  disabled={loading}
                  style={{ background: '#f59e0b', width: '100%', fontSize: '13px', padding: '8px 12px' }}
                >
                  {loading ? 'Resetting...' : 'Reset & Re-implement'}
                </button>
              </div>
            )}
          </div>
        );
      }

      case 'review': {
        const prUrl = task.pipeline?.review?.pr_url || task.job?.pr_url;
        const implCompleted = task.pipeline?.implement?.status === 'completed';
        return (
          <div className="agent-detail__content">
            {renderStuckMessage()}
            {prUrl && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Pull Request</span>
                <a
                  href={prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="agent-detail__pr-link"
                >
                  View Pull Request
                </a>
              </div>
            )}
            {task.review_job && !stuck && agentStatus !== 'failed' && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Review Job</span>
                <p className="agent-detail__info-value">
                  Status: {task.review_job.status}
                  {task.review_job.error_message && (
                    <span className="agent-detail__error-inline"> — {task.review_job.error_message}</span>
                  )}
                </p>
              </div>
            )}
            {agentStatus === 'pending' && prUrl && (
              <button
                className="agent-detail__action-btn"
                onClick={handleReview}
                disabled={loading || !!task.review_job}
                style={{ background: agentDef.color }}
              >
                {loading ? 'Submitting...' : 'Run Review'}
              </button>
            )}
            {agentStatus === 'pending' && !prUrl && implCompleted && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Missing PR URL</span>
                <p className="agent-detail__info-value" style={{ marginBottom: '8px' }}>
                  Implementation completed but no PR URL was captured. Paste the PR URL to run the review.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={manualPrUrl}
                    onChange={(e) => setManualPrUrl(e.target.value)}
                    placeholder="https://github.com/org/repo/pull/123"
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color, #334155)',
                      background: 'var(--bg-secondary, #1e293b)',
                      color: 'var(--text-primary, #e2e8f0)',
                    }}
                  />
                  <button
                    className="agent-detail__action-btn"
                    onClick={handleSetPrUrl}
                    disabled={loading || !manualPrUrl.trim()}
                    style={{ background: agentDef.color, whiteSpace: 'nowrap' }}
                  >
                    {loading ? '...' : 'Set URL'}
                  </button>
                </div>
              </div>
            )}
            {agentStatus === 'pending' && !prUrl && !implCompleted && (
              <div className="agent-detail__info">
                <span className="agent-detail__info-label">Status</span>
                <p className="agent-detail__info-value">
                  Waiting for implementation to complete
                </p>
              </div>
            )}
            {agentStatus === 'running' && !stuck && (
              <div className="agent-detail__running-msg">
                Code review in progress...
              </div>
            )}
            {agentStatus === 'completed' && (
              <div className="agent-detail__completed-msg">
                {task.pipeline?.review?.changes_requested
                  ? 'Changes requested — re-implement needed'
                  : 'Code review complete — approved'}
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="agent-detail">
      <div className="agent-detail__header">
        <div className="agent-detail__agent-name">
          <span style={{ color: agentDef?.color, marginRight: '8px', fontSize: '16px' }}>{agentDef?.icon}</span>
          {agentDef?.label} Agent
        </div>
        <span
          className="agent-detail__badge"
          style={{ background: badge.bg, color: badge.color }}
        >
          {badge.label}
        </span>
      </div>

      {error && <div className="alert alert-error" style={{ margin: '12px 0', fontSize: '13px' }}>{error}</div>}

      {renderAgentContent()}
    </div>
  );
};

export default AgentDetailPanel;
